<script lang="ts">
export default {
  name: 'ReportingHub',
};
</script>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, provide, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reportingService, ReportingTableauType } from '@/services/ReportingService';
import { TableauDashboard } from '@/types/TableauDashboard';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Document, DocumentType } from '@/types/v2/Document';
import { Factory, Wrench, ChartBarIcon, FileTextIcon, StarIcon } from '@lucide/vue';
import { useFavorites } from '@/composables/useFavorites';
import type { ReportingType } from '@/services/ReportingService';
import { documentService } from '@/services/v2/DocumentService';

// -----
// data
// -----

const tableauVizHeight = ref('');
const resourceTypeHeader = ref<HTMLElement | null>(null);
const loading = ref<boolean>(true);
const reportingTypes = ref<ReportingType[]>([]);
const dashboards = ref<TableauDashboard[]>([]);
const documents = ref<Document[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const tableauTypes = ref<ReportingTableauType[]>([]);
const selectedCategoryType = ref<string>('');
const store = inject<Store<AppState>>('store');
const router = useRouter();
const route = useRoute();
const activeFilters = ref<string[]>([]);
const { fetchFavorites, isFavorited, toggleFavorite, getFavoritedIds } = useFavorites();

// ---------
// watchers
// ---------
watchEffect(() => {
  // Go back to the dashboard if the user navigates to the reporting hub
  if (route.path === '/reporting') {
    selectedCategoryType.value = '';
  }
});

watch(
  () => route.params.categoryId,
  async (category) => {
    if (category && category !== 'favorites') {
      selectedCategoryType.value = category as string;
    }
  },
);

// -----------
// lifecycles
// -----------

onMounted(async () => {
  loading.value = true;

  // Fetch reporting types from the service
  await Promise.all([fetchReportingTypes(), fetchFavorites()]);

  if (route.params.categoryId && route.params.categoryId !== 'favorites') {
    selectedCategoryType.value = route.params.categoryId as string;
  }

  await refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;
  computeTableauHeight();
  await Promise.all([fetchReportingTableauDashboards(), fetchDocuments(), fetchDocumentTypes(), fetchTableauTypes()]);

  loading.value = false;
}

async function fetchReportingTypes() {
  try {
    reportingTypes.value = await reportingService.getReportingTypes();
  } catch (error) {
    console.error('Error fetching reporting types:', error);
    console.error('Using default reporting types');
    // If the service fails, create default types
    reportingTypes.value = [
      { name: 'Production', description: 'Production related reports' },
      { name: 'Operations', description: 'Operations related reports' },
      { name: 'Data Quality', description: 'Data quality reports' },
    ];
  }
}

async function fetchReportingTableauDashboards() {
  dashboards.value = await reportingService.getReportingTableauDashboards();
}

async function fetchDocuments() {
  documents.value = await reportingService.getReportingDocuments();
}

async function fetchDocumentTypes() {
  documentTypes.value = (await documentService.getDocumentTypes()).filter((t) => t.domainName === 'Reporting') || [];
}

async function fetchTableauTypes() {
  tableauTypes.value = (await reportingService.getTableauTypes()) || [];
}

function computeTableauHeight() {
  const calculatedHeight =
    window.innerHeight -
    (store?.state.resizerRegistry['header'].height ?? 0) -
    (resourceTypeHeader.value?.offsetHeight ?? 170);
  tableauVizHeight.value = `${calculatedHeight}px`;
}

function handleSelectResourceType(typeName: string) {
  selectedCategoryType.value = typeName;
  router.push({ path: `/reporting/category/${encodeURIComponent(typeName)}` });
  activeFilters.value = [];
}

function handleSelectFavorites() {
  selectedCategoryType.value = '';
  router.push({ path: '/reporting/category/favorites' });
  activeFilters.value = [];
}

function toggleFilter(filter: string) {
  if (activeFilters.value.includes(filter)) {
    activeFilters.value = activeFilters.value.filter((f) => f !== filter);
  } else {
    activeFilters.value.push(filter);
  }
}

function clearFilters() {
  activeFilters.value = [];
}

function updated() {
  refresh();
}

// ---------
// computed
// ---------

const isFavoritesCategory = computed(() => route.params.categoryId === 'favorites');

const filteredDashboards = computed<TableauDashboard[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('reporting-tableau');
    return dashboards.value.filter((d) => favIds.has(String(d.id)));
  }

  const dashboardsByCategoryType = dashboards.value.filter((dashboard) => {
    return dashboard.reportingType === selectedCategoryType.value;
  });

  if (activeFilters.value.length > 0) {
    return dashboardsByCategoryType.filter(
      (dashboard) => dashboard.accountId !== null && activeFilters.value.includes(dashboard.tableauType),
    );
  }

  return dashboardsByCategoryType;
});

const filteredDocuments = computed<Document[]>(() => {
  if (isFavoritesCategory.value) {
    const favIds = getFavoritedIds('reporting-document');
    return documents.value.filter((d) => favIds.has(d.documentId));
  }

  const docsByCategoryType = documents.value.filter((doc) => {
    return doc.reportingType === selectedCategoryType.value;
  });

  if (activeFilters.value.length > 0) {
    return docsByCategoryType.filter((document) => activeFilters.value.includes(document.documentTypeName));
  }

  return docsByCategoryType;
});

const selectedCategoryName = computed<string>(() => {
  if (isFavoritesCategory.value) return 'Favorites';
  return selectedCategoryType.value;
});

const documentsForCategory = computed(() => {
  return documents.value.filter((document) => document.accountId !== null);
});

const dashboardsForCategory = computed(() => {
  return dashboards.value.filter((dashboard) => dashboard.accountId !== null);
});

const visibleDocumentFilters = computed(() => {
  return documentTypes.value.filter((type) =>
    documentsForCategory.value.some((doc) => doc.documentTypeName === type.name),
  );
});

const visibleTableauFilters = computed(() => {
  return tableauTypes.value.filter((type) => dashboardsForCategory.value.some((dashboard) => dashboard.tableauType === type.tableauTypeName));
});

// --------
// provide
// --------
provide('selectedCategoryType', selectedCategoryType);
provide('isFavoritesCategory', isFavoritesCategory);
provide('isFavorited', isFavorited);
provide('toggleFavorite', toggleFavorite);
</script>

<template>
  <div v-if="!selectedCategoryType && !isFavoritesCategory" class="m-auto flex flex-col items-center gap-4 pt-8">
    <div class="flex flex-col items-center">
      <h2>Reporting Hub</h2>
      <div>Please choose from one of the categories below</div>
    </div>

    <div class="flex w-120 flex-wrap justify-center gap-4 pt-2">
      <div class="group tooltip pointer-events-auto z-20 cursor-pointer">
        <div
          class="flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
          @click="handleSelectFavorites()">
          <div class="text-black-500 flex flex-col items-center gap-2 self-center p-2">
            <StarIcon class="text-gray-400" />
            <h2 class="text-center text-sm font-medium">Favorites</h2>
          </div>
        </div>
      </div>
      <template v-for="reportingType in reportingTypes" :key="reportingType.name">
        <div class="group tooltip pointer-events-auto z-20 cursor-pointer">
          <div
            class="flex h-24 w-36 flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none"
            @click="handleSelectResourceType(reportingType.name)">
            <div class="text-black-500 flex flex-col items-center gap-2 self-center p-2">
              <Factory v-if="reportingType.name === 'Production'" class="text-gray-400" />
              <Wrench v-if="reportingType.name === 'Operations'" class="text-gray-400" />
              <ChartBarIcon v-if="reportingType.name === 'Data Quality'" class="text-gray-400" />
              <FileTextIcon
                v-if="!['Production', 'Operations', 'Data Quality'].includes(reportingType.name)"
                class="text-gray-400" />

              <h2 class="text-center text-sm font-medium">{{ reportingType.name }}</h2>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <div v-if="selectedCategoryType || isFavoritesCategory" class="relative flex h-full">
    <aside class="w-60 border-r border-gray-300 bg-gray-100 p-2">
      <nav class="sticky top-20 w-60 p-2">
        <h2 class="text-xl text-primary">Categories</h2>

        <div class="mt-4">
          <div
            role="button"
            class="m-1 mr-3 rounded-md border border-gray-100 p-1 px-2 text-lg transition-all select-none hover:border-gray-300 hover:bg-gray-50"
            :class="{
              'bg-specialty-teal/30 hover:bg-specialty-teal/30': isFavoritesCategory,
            }"
            @click="handleSelectFavorites()">
            Favorites
          </div>
          <div
            v-for="(reportingType, i) in reportingTypes"
            :key="reportingType.name"
            :aria-label="reportingType.name"
            :tabindex="i"
            role="button"
            class="m-1 mr-3 rounded-md border border-gray-100 p-1 px-2 text-lg transition-all select-none hover:border-gray-300 hover:bg-gray-50"
            :class="{
              'bg-specialty-teal/30 hover:bg-specialty-teal/30': selectedCategoryType === reportingType.name && !isFavoritesCategory,
            }"
            @click="handleSelectResourceType(reportingType.name)">
            {{ reportingType.name }}
          </div>
        </div>

        <!-- filters -->
        <div v-if="!isFavoritesCategory">
          <div class="mt-4 flex items-center gap-2">
            <h2 class="text-xl text-primary">Filter by Type</h2>
            <button
              v-if="activeFilters.length > 0"
              class="btn rounded-lg bg-specialty-teal/30 btn-xs"
              @click="clearFilters">
              Clear All
            </button>
          </div>
          <div class="mt-4 flex flex-col gap-1 p-3">
            <div class="flex flex-wrap justify-center gap-2">
              <!-- Document Filters -->
              <div
                v-for="(type, i) in visibleDocumentFilters"
                :key="type.id"
                :aria-label="type.name"
                :tabindex="i"
                data-testid="doc-filter"
                role="button"
                class="text-md btn rounded shadow-md transition-all duration-75 hover:bg-gray-50"
                :class="{
                  'bg-specialty-teal/30 hover:bg-specialty-teal/30': activeFilters.includes(type.name),
                }"
                @click="toggleFilter(type.name)">
                {{ type.name }}
              </div>

              <!-- Tableau Type Filters -->
              <div
                v-for="(type, i) in visibleTableauFilters"
                :key="type.tableauTypeId"
                :aria-label="type.tableauTypeName"
                :tabindex="i"
                role="button"
                class="filter-button text-md border-gray-200 shadow-md transition-all duration-75 hover:bg-gray-50"
                :class="{
                  'bg-specialty-teal/30 hover:bg-specialty-teal/30': activeFilters.includes(type.tableauTypeName),
                }"
                @click="toggleFilter(type.tableauTypeName)">
                {{ type.tableauTypeName }}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </aside>

    <router-view
      :loading="loading"
      :dashboards="filteredDashboards"
      :document-types="documentTypes"
      :tableau-types="tableauTypes"
      :documents="filteredDocuments"
      :selectedCategoryName="selectedCategoryName"
      @updated="updated" />
  </div>
</template>
